import { useAlternativeTransportPlans } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/useAlternativeTransportPlans";
import CollapsibleSection from "features/CenterContent/RoleContent/EventHandling/components/CollapsibleSection";
import {
  PLAN_MEASURE_KEYS,
  PlanEventInformation,
  PlanMeasures,
} from "features/CenterContent/RoleContent/EventHandling/components/EventChangesDialog/types";
import { EventStateColumn } from "features/CenterContent/RoleContent/EventHandling/components/EventStateColumn";
import { EventStateHeader } from "features/CenterContent/RoleContent/EventHandling/components/EventStateHeader";
import { SectionEmptyState } from "features/CenterContent/RoleContent/EventHandling/components/SectionEmptyState";
import { ShowClosedToggle } from "features/CenterContent/RoleContent/EventHandling/components/ShowClosedToggle";
import { Subgrid } from "features/CenterContent/RoleContent/EventHandling/components/Subgrid";
import { useEffect, useMemo, useState } from "react";
import { Grid, HStack, Skeleton, tokens } from "shared/components/Spor";
import styled, { useTheme } from "styled-components";

const EventHandlingWrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: ${tokens.default.size.spacing[3]};

  display: flex;
  flex-direction: column;
  gap: ${tokens.default.size.spacing[3]};

  background-color: ${({ theme }) => theme.colorBackgroundMain};

  border-radius: ${tokens.default.size.spacing[1]};
  border: 1px solid ${({ theme }) => theme.colorOutlineSecondary};

  overflow: auto;
`;

// eslint-disable-next-line arrow-body-style -- It is clearer that this function returns another function this way
const reducerFactory = (showClosed: boolean) => {
  return (prevCount: number, currentMeasures: PlanEventInformation[]): number =>
    prevCount +
    currentMeasures.filter(
      (m) => showClosed || m.information.state !== "CLOSED",
    ).length;
};

const planMeasureCount = (
  planMeasures: PlanMeasures | undefined,
  showClosed: boolean,
) => {
  if (!planMeasures) return 0;

  return Object.values(planMeasures).reduce(reducerFactory(showClosed), 0);
};

const EventHandling = () => {
  const {
    data: plansResponse,
    isPending: isPlansLoading,
    refetch,
  } = useAlternativeTransportPlans();

  const infrastructureData = plansResponse?.data?.infrastructure;
  const trainData = plansResponse?.data?.train;

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const theme = useTheme();

  const [showClosedInfraMeasures, setShowClosedInfraMeasures] = useState(false);
  const [showClosedTrainMeasures, setShowClosedTrainMeasures] = useState(false);

  const infrastructureDisplayCount = useMemo(
    () => planMeasureCount(infrastructureData, showClosedInfraMeasures),
    [infrastructureData, showClosedInfraMeasures],
  );
  const trainDisplayCount = useMemo(
    () => planMeasureCount(trainData, showClosedTrainMeasures),
    [trainData, showClosedTrainMeasures],
  );

  if (isPlansLoading) {
    return <Skeleton height="100%" />;
  }

  return (
    <EventHandlingWrapper>
      <Grid
        templateColumns="repeat(4, 1fr)"
        templateRows="[header] auto [content] 1fr"
        gap="2"
        minWidth="1400px"
        overflow="hidden"
      >
        <Subgrid gridRow="content" overflow="hidden">
          <Subgrid overflowY="scroll">
            <CollapsibleSection
              label="Infrastruktur"
              counter={infrastructureDisplayCount}
              rightContent={
                <ShowClosedToggle
                  showClosed={showClosedInfraMeasures}
                  onToggle={() =>
                    setShowClosedInfraMeasures(!showClosedInfraMeasures)
                  }
                  disabled={
                    !PLAN_MEASURE_KEYS.some((state) =>
                      infrastructureData?.[state].some(
                        ({ information }) => information.state === "CLOSED",
                      ),
                    )
                  }
                />
              }
              backgroundColor={theme.colorBackgroundMain}
            >
              {infrastructureDisplayCount > 0 ? (
                <Subgrid gap="2">
                  {PLAN_MEASURE_KEYS.map((state) => (
                    <EventStateColumn
                      key={state}
                      measures={infrastructureData?.[state] ?? []}
                      columnEventState={state}
                      showClosed={showClosedInfraMeasures}
                    />
                  ))}
                </Subgrid>
              ) : (
                <SectionEmptyState message="Ingen hendelser knyttet til infrastruktur" />
              )}
            </CollapsibleSection>

            <CollapsibleSection
              label="Tog"
              mt={tokens.default.size.spacing[2]}
              counter={trainDisplayCount}
              backgroundColor={theme.colorBackgroundMain}
              rightContent={
                <ShowClosedToggle
                  showClosed={showClosedTrainMeasures}
                  onToggle={() =>
                    setShowClosedTrainMeasures(!showClosedTrainMeasures)
                  }
                  disabled={
                    !PLAN_MEASURE_KEYS.some((state) =>
                      trainData?.[state].some(
                        ({ information }) => information.state === "CLOSED",
                      ),
                    )
                  }
                />
              }
            >
              {trainDisplayCount > 0 ? (
                <Subgrid gap="2">
                  {PLAN_MEASURE_KEYS.map((state) => (
                    <EventStateColumn
                      key={state}
                      measures={trainData?.[state] ?? []}
                      columnEventState={state}
                      showClosed={showClosedTrainMeasures}
                    />
                  ))}
                </Subgrid>
              ) : (
                <SectionEmptyState message="Ingen hendelser knyttet til tog" />
              )}
            </CollapsibleSection>
          </Subgrid>
        </Subgrid>

        <HStack
          gridColumn="1 / -1"
          gridRow="header"
          position="sticky"
          top="0"
          gap="2"
        >
          {PLAN_MEASURE_KEYS.map((measureKey) => (
            <EventStateHeader
              key={measureKey}
              state={measureKey}
              infraMeasures={infrastructureData?.[measureKey]}
              trainMeasures={trainData?.[measureKey]}
              showClosedInfraMeasures={showClosedInfraMeasures}
              showClosedTrainMeasures={showClosedTrainMeasures}
            />
          ))}
        </HStack>
      </Grid>
    </EventHandlingWrapper>
  );
};

export default EventHandling;
